import { useEffect, useState } from "react";
import { CalendarCheck, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/constants";
import { PageHeader, EmptyState, Stat } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/DatePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const KINDS = { leave: "Leave", half_day: "Half day", unavailable: "Not available for visits", training: "Training / off-site" };
const TONE = { leave: "bg-rose-100 text-rose-700 border-rose-200", half_day: "bg-amber-100 text-amber-800 border-amber-200", unavailable: "bg-slate-100 text-slate-700 border-slate-200", training: "bg-blue-100 text-blue-800 border-blue-200" };
const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { member_id: "", kind: "leave", from_date: today(), to_date: today(), reason: "" };

export default function Availability() {
  const [items, setItems] = useState(null);
  const [team, setTeam] = useState([]);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/coll/availability").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => {
    load();
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const nameOf = (id) => team.find((t) => t.id === id)?.name || "Unknown";

  const add = async (e) => {
    e.preventDefault();
    if (!form.member_id) { toast.error("Pick a team member"); return; }
    try {
      await api.post("/coll/availability", { ...form, member_name: nameOf(form.member_id) });
      toast.success("Entry added");
      setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/coll/availability/${id}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!items) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading availability…</div>;

  const t = today();
  const upcoming = items.filter((i) => (i.to_date || i.from_date) >= t).sort((a, b) => a.from_date.localeCompare(b.from_date));
  const past = items.filter((i) => (i.to_date || i.from_date) < t).sort((a, b) => b.from_date.localeCompare(a.from_date));
  const outToday = items.filter((i) => i.from_date <= t && (i.to_date || i.from_date) >= t);

  return (
    <div className="space-y-6" data-testid="availability-page">
      <PageHeader overline="Team" title="Attendance / Availability" subtitle="Leave and site-visit availability — feeds into how you plan the Site Visit Calendar" />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat label="Out today" value={outToday.length} tone={outToday.length ? "text-rose-600" : "text-emerald-600"} sub={outToday.map((i) => i.member_name).join(", ") || "Everyone available"} testid="stat-out-today" />
        <Stat label="Upcoming entries" value={upcoming.length} testid="stat-upcoming" />
        <Stat label="Team size" value={team.length} testid="stat-team-size" />
      </div>

      <Card className="p-6 border-border">
        <form onSubmit={add} className="grid sm:grid-cols-5 gap-3 items-end" data-testid="availability-form">
          <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Team Member</Label>
            <Select value={form.member_id} onValueChange={set("member_id")}>
              <SelectTrigger data-testid="av-member"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} · {ROLE_LABELS[m.role] || m.role}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Type</Label>
            <Select value={form.kind} onValueChange={set("kind")}>
              <SelectTrigger data-testid="av-kind"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(KINDS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">From</Label><DatePicker value={form.from_date} onChange={set("from_date")} placeholder="Select date" className="w-full" data-testid="av-from" /></div>
          <div className="space-y-1.5"><Label className="text-xs">To</Label><DatePicker value={form.to_date} onChange={set("to_date")} placeholder="Select date" className="w-full" data-testid="av-to" /></div>
          <div className="space-y-1.5 sm:col-span-4"><Label className="text-xs">Reason (optional)</Label><Input value={form.reason} onChange={set("reason")} data-testid="av-reason" /></div>
          <Button type="submit" className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="add-availability-btn"><Plus size={15} className="mr-1" /> Add</Button>
        </form>
      </Card>

      <div>
        <div className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-3">Upcoming & current</div>
        {upcoming.length === 0 ? (
          <EmptyState icon={CalendarCheck}>No leave or unavailability logged. Everyone is available for site visits.</EmptyState>
        ) : (
          <div className="space-y-2">
            {upcoming.map((i) => (
              <Card key={i.id} className="p-3 flex flex-wrap items-center justify-between gap-3 border-border" data-testid={`av-item-${i.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-primary truncate">{i.member_name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{i.from_date}{i.to_date && i.to_date !== i.from_date ? ` → ${i.to_date}` : ""}{i.reason ? ` · ${i.reason}` : ""}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] px-2 py-0.5 rounded border ${TONE[i.kind] || TONE.unavailable}`}>{KINDS[i.kind] || i.kind}</span>
                  <button className="text-destructive" onClick={() => remove(i.id)} data-testid={`delete-av-${i.id}`}><Trash size={15} /></button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {past.length > 0 && (
        <div>
          <div className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-3">Past</div>
          <div className="space-y-1.5">
            {past.slice(0, 15).map((i) => (
              <div key={i.id} className="flex items-center justify-between gap-2 p-2 rounded bg-muted text-sm" data-testid={`av-past-${i.id}`}>
                <span className="text-muted-foreground truncate">{i.member_name} · {KINDS[i.kind] || i.kind}</span>
                <span className="text-xs font-mono text-muted-foreground shrink-0">{i.from_date}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
